import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// Base locales (complete translations)
import ptBR from './pt-BR.json'
import enUS from './en-US.json'
import de from './de.json'
import fr from './fr.json'
import it from './it.json'
import es from './es.json'
import nl from './nl.json'
import ja from './ja.json'
import hi from './hi.json'
import ar from './ar.json'
import sv from './sv.json'
import pl from './pl.json'
import ms from './ms.json'
import th from './th.json'
import ro from './ro.json'
import he from './he.json'

// Variant locales (override specific keys from their base)
import ptPT from './pt-PT.json'
import enGB from './en-GB.json'
import enAU from './en-AU.json'
import enNZ from './en-NZ.json'
import enSG from './en-SG.json'
import enNG from './en-NG.json'
import enPH from './en-PH.json'
import enZA from './en-ZA.json'
import enCA from './en-CA.json'
import arAE from './ar-AE.json'
import deCH from './de-CH.json'
import frBE from './fr-BE.json'
import esMX from './es-MX.json'
import esAR from './es-AR.json'
import esCO from './es-CO.json'
import esCL from './es-CL.json'

// Deep merge: variant overrides only the keys it defines, preserving all base keys
function deepMerge(base: any, override: any): any {
  const result = { ...base }
  for (const key of Object.keys(override)) {
    if (
      override[key] &&
      typeof override[key] === "object" &&
      !Array.isArray(override[key]) &&
      base[key] &&
      typeof base[key] === "object"
    ) {
      result[key] = deepMerge(base[key], override[key])
    } else {
      result[key] = override[key]
    }
  }
  return result
}

const resources: Record<string, { translation: any }> = {
  'pt-BR': { translation: ptBR },
  'en-US': { translation: enUS },
  'de': { translation: de },
  'fr': { translation: fr },
  'it': { translation: it },
  'es': { translation: es },
  'nl': { translation: nl },
  'ja': { translation: ja },
  'hi': { translation: hi },
  'ar': { translation: ar },
  'sv': { translation: sv },
  'pl': { translation: pl },
  'ms': { translation: ms },
  'th': { translation: th },
  'ro': { translation: ro },
  'he': { translation: he },
  // Variants inherit from base, deep-merging overrides
  'pt-PT': { translation: deepMerge(ptBR, ptPT) },
  'en-GB': { translation: deepMerge(enUS, enGB) },
  'en-AU': { translation: deepMerge(enUS, enAU) },
  'en-NZ': { translation: deepMerge(enUS, enNZ) },
  'en-SG': { translation: deepMerge(enUS, enSG) },
  'en-NG': { translation: deepMerge(enUS, enNG) },
  'en-PH': { translation: deepMerge(enUS, enPH) },
  'en-ZA': { translation: deepMerge(enUS, enZA) },
  'en-CA': { translation: deepMerge(enUS, enCA) },
  'ar-AE': { translation: deepMerge(ar, arAE) },
  'de-CH': { translation: deepMerge(de, deCH) },
  'fr-BE': { translation: deepMerge(fr, frBE) },
  'es-MX': { translation: deepMerge(es, esMX) },
  'es-AR': { translation: deepMerge(es, esAR) },
  'es-CO': { translation: deepMerge(es, esCO) },
  'es-CL': { translation: deepMerge(es, esCL) },
}

// Detect language: ?lang= param > localStorage > timezone-BR > browser > default
//
// Timezone fallback antes do browser pra resolver caso comum:
// brasileiro com Mac/Windows configurado em inglês (navigator.language='en-US')
// mas Timezone "America/Sao_Paulo" — antes caía em en-US e via hero em inglês,
// matando o pitch antes do clique. Agora detecta intenção real via TZ.
//
// Cobre os 4 timezones brasileiros (UTC-2 a UTC-5) — Fernando de Noronha,
// horário oficial BR, Manaus, Acre — todos com prefixo `America/` + cidade BR.
function detectLanguage(): string {
  const urlParams = new URLSearchParams(window.location.search)
  const langParam = urlParams.get('lang')
  if (langParam && resources[langParam]) return langParam

  const saved = localStorage.getItem('pakt_language')
  if (saved && resources[saved]) return saved

  // Timezone-based BR detection — antes do browser language porque
  // mac/windows BR configurado em inglês reporta navigator.language='en-US'
  // mas timezone continua sendo America/Sao_Paulo. TZ é mais confiável
  // pra inferir país real do visitante.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const BR_CITIES = [
      'Sao_Paulo', 'Bahia', 'Fortaleza', 'Manaus', 'Recife', 'Belem',
      'Cuiaba', 'Campo_Grande', 'Maceio', 'Noronha', 'Porto_Velho',
      'Boa_Vista', 'Rio_Branco', 'Santarem', 'Eirunepe', 'Araguaina',
    ]
    if (tz.startsWith('America/') && BR_CITIES.some(c => tz.includes(c))) {
      return 'pt-BR'
    }
  } catch {
    // Intl indisponível (browsers muito antigos) — segue pro fallback browserLang
  }

  const browserLang = navigator.language
  if (resources[browserLang]) return browserLang
  const base = browserLang.split('-')[0]
  const match = Object.keys(resources).find(k => k === base || k.startsWith(base + '-'))
  if (match) return match

  return 'en-US'
}

const detectedLng = detectLanguage()

if (['ar', 'he'].some(r => detectedLng.startsWith(r))) {
  document.documentElement.dir = 'rtl'
} else {
  document.documentElement.dir = 'ltr'
}
document.documentElement.lang = detectedLng

if (new URLSearchParams(window.location.search).get('lang')) {
  localStorage.setItem('pakt_language', detectedLng)
}

i18n.use(initReactI18next).init({
  resources,
  lng: detectedLng,
  fallbackLng: 'en-US',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

export default i18n
